// hooks/useOrders.js
// Orders Hook - Synchronized with Mobile App
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../services/api";
import { invalidateNotificationQueries } from "../utils/notificationHelpers";
import { useAuthStore } from "../stores/authStore";

export const useOrders = () => {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Fetch all orders
  const {
    data: ordersData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["orders"],
    queryFn: async () => {
      const response = await apiService.getOrders();
      return response.data || [];
    },
    enabled: !!isAuthenticated,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: false,
  });

  // Create order mutation
  const createOrderMutation = useMutation({
    mutationFn: async (orderData) => {
      return await apiService.createOrder(orderData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["cart"] });
      invalidateNotificationQueries(queryClient);
    },
  });

  // Cancel order mutation
  const cancelOrderMutation = useMutation({
    mutationFn: ({ orderId, reason }) => apiService.cancelOrder(orderId, reason),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      if (variables?.orderId) {
        queryClient.invalidateQueries({ queryKey: ["orders", variables.orderId] });
      }
      invalidateNotificationQueries(queryClient);
    },
  });

  // Delete order mutation
  const deleteOrderMutation = useMutation({
    mutationFn: (orderId) => apiService.deleteOrder(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });

  return {
    // Data
    orders: ordersData || [],
    ordersCount: ordersData?.length || 0,

    // States
    isLoading,
    isError,
    error,

    // Actions
    refetch,
    createOrder: createOrderMutation.mutate,
    createOrderAsync: createOrderMutation.mutateAsync,
    cancelOrder: cancelOrderMutation.mutate,
    cancelOrderAsync: cancelOrderMutation.mutateAsync,
    deleteOrder: deleteOrderMutation.mutate,
    deleteOrderAsync: deleteOrderMutation.mutateAsync,

    // Mutation states
    isCreatingOrder: createOrderMutation.isPending,
    isCancellingOrder: cancelOrderMutation.isPending,
    isDeletingOrder: deleteOrderMutation.isPending,
    orderCreationError: createOrderMutation.error,
    isOrderCreationSuccess: createOrderMutation.isSuccess,
    createdOrder: createOrderMutation.data,
  };
};

// Get single order by ID
export const useOrder = (orderId) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery({
    queryKey: ["orders", orderId],
    queryFn: async () => {
      const response = await apiService.getOrder(orderId);
      return response.data || null;
    },
    enabled: !!isAuthenticated && !!orderId,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: false,
  });
};

// Track order
export const useTrackOrder = (orderId) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery({
    queryKey: ["orders", orderId, "tracking"],
    queryFn: async () => {
      const response = await apiService.trackOrder(orderId);
      return response.data || null;
    },
    enabled: !!isAuthenticated && !!orderId,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: false,
  });
};

export default useOrders;

